import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Film } from 'lucide-react';

const CREDIT_TIERS = [
  { credits: 5, price: 5, priceId: process.env.REACT_APP_CREDITS_5, perCredit: '1.00' },
  { credits: 25, price: 20, priceId: process.env.REACT_APP_CREDITS_25, perCredit: '0.80' },
  { credits: 50, price: 40, priceId: process.env.REACT_APP_CREDITS_50, perCredit: '0.80', badge: 'POPULAR', badgeColor: '#dc3545' },
  { credits: 100, price: 75, priceId: process.env.REACT_APP_CREDITS_100, perCredit: '0.75', badge: 'BEST VALUE', badgeColor: '#28a745' },
  { credits: 250, price: 150, priceId: process.env.REACT_APP_CREDITS_250, perCredit: '0.60' },
];

export default function BuyCreditsModal({ isOpen, onClose, currentBalance, cardLast4, onPurchaseComplete }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handlePurchase = async () => {
    if (!selectedTier || !confirmed) return;

    setPurchasing(true);
    setError('');

    try {
      const functions = getFunctions();
      const purchaseVideoCredits = httpsCallable(functions, 'purchaseVideoCredits');
      const result = await purchaseVideoCredits({ priceId: selectedTier.priceId });
      const data = result.data;

      if (!data.success) {
        setError(data.error || 'Purchase failed');
        setPurchasing(false);
        return;
      }

      // Reset state and notify parent
      setSelectedTier(null);
      setConfirmed(false);
      if (onPurchaseComplete) {
        onPurchaseComplete(data.newBalance, data.creditsAdded, data.amount);
      }
      onClose();
    } catch (err) {
      console.error('Purchase error:', err);
      setError('Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleClose = () => {
    setSelectedTier(null);
    setConfirmed(false);
    setError('');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999,
    }} onClick={handleClose}>
      <div style={{
        width: '100%', maxWidth: '420px', background: 'white', borderRadius: '12px',
        overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Film size={22} color="#dc3545" />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a1a2e' }}>Buy Video Credits</h3>
          </div>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#999', padding: '4px',
          }}>✕</button>
        </div>

        <p style={{ margin: '6px 24px 20px', fontSize: '13px', color: '#666' }}>
          Credits never expire. Use them to generate branded exercise videos.
        </p>

        {/* Tier selection */}
        <div style={{ padding: '0 24px 20px' }}>
          {CREDIT_TIERS.map((tier) => (
            <div
              key={tier.credits}
              onClick={() => { setSelectedTier(tier); setError(''); }}
              style={{
                padding: '14px 16px', border: `2px solid ${selectedTier?.credits === tier.credits ? '#dc3545' : '#e0e0e0'}`,
                borderRadius: '10px', marginBottom: '10px', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                transition: 'border-color 0.15s',
              }}
            >
              <div>
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a2e' }}>{tier.credits} credits</span>
                {tier.badge && (
                  <span style={{ fontSize: '11px', color: tier.badgeColor, marginLeft: '6px', fontWeight: 500 }}>
                    {tier.badge}
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a2e' }}>${tier.price}</span>
                <span style={{ fontSize: '12px', color: '#999', marginLeft: '4px' }}>${tier.perCredit}/ea</span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <span style={{ fontSize: '13px', color: '#666' }}>Current balance: </span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a2e' }}>{currentBalance ?? 0} credits</span>
            </div>
            {cardLast4 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <span style={{ fontSize: '13px', color: '#666' }}>•••• {cardLast4}</span>
              </div>
            )}
          </div>

          {/* Confirmation checkbox */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '14px', cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ marginTop: '2px', accentColor: '#dc3545', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px', color: '#666', lineHeight: 1.4 }}>
              I confirm this one-time purchase. Credits are non-refundable and will be added to my account immediately.
            </span>
          </label>

          {error && (
            <div style={{
              padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: '#dc3545',
            }}>
              {error}
            </div>
          )}

          {/* Purchase button */}
          <button
            onClick={handlePurchase}
            disabled={!selectedTier || !confirmed || purchasing}
            style={{
              width: '100%', padding: '12px', background: '#dc3545', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
              cursor: (!selectedTier || !confirmed || purchasing) ? 'not-allowed' : 'pointer',
              opacity: (!selectedTier || !confirmed || purchasing) ? 0.5 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {purchasing
              ? 'Processing...'
              : selectedTier
                ? `Purchase ${selectedTier.credits} credits for $${selectedTier.price}`
                : 'Select a tier above'
            }
          </button>
          {selectedTier && (
            <p style={{ textAlign: 'center', margin: '8px 0 0', fontSize: '11px', color: '#999' }}>
              ${selectedTier.perCredit} per credit
            </p>
          )}
        </div>

      </div>
    </div>
  );
}